import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchTournamentLeaderboard } from '../../../services/tournamentService';
import TournamentPerformanceTabs from '../components/TournamentPerformanceTabs';
import PageShell from '../../../components/layout/PageShell';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const TournamentLeaderboardPage = () => {
  const { organizationId, managerId, tournamentId } = useParams();
  const { token } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [summary, setSummary] = useState(null);
  const [leaders, setLeaders] = useState(null);
  const [performanceLeaders, setPerformanceLeaders] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadLeaderboard = async () => {
      if (!token || !tournamentId) return;

      setIsLoading(true);
      setError('');

      try {
        const response = await fetchTournamentLeaderboard(tournamentId, token, organizationId);
        setTournament(response.tournament || null);
        setLeaderboard(response.leaderboard || []);
        setSummary(response.summary || null);
        setLeaders(response.leaders || null);
        setPerformanceLeaders(response.leaders?.performanceLeaders || null);
      } catch (loadError) {
        setTournament(null);
        setLeaderboard([]);
        setSummary(null);
        setLeaders(null);
        setPerformanceLeaders(null);
        setError(loadError.message || 'Unable to load leaderboard.');
      } finally {
        setIsLoading(false);
      }
    };

    loadLeaderboard();
  }, [organizationId, token, tournamentId]);

  const backLink = useMemo(() => {
    if (organizationId && managerId && tournamentId) {
      return `/org/${organizationId}/match-manager/${managerId}/tournaments/${tournamentId}`;
    }
    if (organizationId && tournamentId) {
      return `/org/${organizationId}/managers/tournaments/${tournamentId}`;
    }
    return '/management';
  }, [managerId, organizationId, tournamentId]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <PageShell containerClassName="p-0">
    <div className="py-4">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
        <div>
          <p className="eyebrow text-primary mb-1">Tournament leaderboard</p>
          <h1 className="h4 mb-1">{tournament?.name || 'Leaderboard standings'}</h1>
          <p className="text-muted mb-0">
            Track wins, points, and net run rate across completed matches.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Link className="btn btn-sm btn-outline-secondary" to={backLink}>
            Back to management
          </Link>
          {tournamentId ? (
            <Link className="btn btn-sm btn-outline-primary" to={`/public/tournaments/${tournamentId}/leaderboard`}>
              View public leaderboard
            </Link>
          ) : null}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {isLoading && (
        <div className="alert alert-secondary" role="alert">
          Loading leaderboard...
        </div>
      )}

      {!isLoading && summary && (
        <div className="alert alert-info d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center">
          <span>
            Completed {summary.completedMatches} of {summary.totalMatches} scheduled matches.
          </span>
          <span className="text-muted small">Updated after the latest completed match.</span>
        </div>
      )}


      <TournamentPerformanceTabs leaders={leaders} performanceLeaders={performanceLeaders} teamLeaderboard={leaderboard} />
    </div>
  
    </PageShell>
  );
};

export default TournamentLeaderboardPage;
